import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { ChevronLeft, MapPin, Navigation, Search, Loader2, Check } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom marker icon with Fast Max brand color
const customMarkerIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="40" height="60">
      <path fill="#B1336E" stroke="#ffffff" stroke-width="2" d="M12 0C7.03 0 3 4.03 3 9c0 6.75 9 18 9 18s9-11.25 9-18c0-4.97-4.03-9-9-9z"/>
      <circle fill="#ffffff" cx="12" cy="9" r="4"/>
    </svg>
  `),
  iconSize: [40, 60],
  iconAnchor: [20, 60],
  popupAnchor: [0, -60],
});

// Map click handler component
function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng);
    },
  });
  return null;
}

function MapSelectionDriver({ setActiveComponent, userId }) {
  const [markerPosition, setMarkerPosition] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [error, setError] = useState('');
  const mapRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Default center: Lima, Peru
  const defaultCenter = [-12.0464, -77.0428];

  // Fetch address suggestions from Google Places API
  const fetchAddressSuggestions = async (input) => {
    if (!input.trim() || input.length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);

    try {
      const autocompleteService = new window.google.maps.places.AutocompleteService();
      autocompleteService.getPlacePredictions(
        {
          input,
          componentRestrictions: { country: 'pe' },
          location: new window.google.maps.LatLng(-12.0464, -77.0428),
          radius: 50000,
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSearchResults(predictions);
          } else {
            setSearchResults([]);
          }
          setIsSearching(false);
        }
      );
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setSearchResults([]);
      setIsSearching(false);
    }
  };

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchAddressSuggestions(value);
    }, 300);
  };

  // Handle search result selection
  const handleResultClick = (result) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: result.place_id }, (results, status) => {
      if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
        const location = results[0].geometry.location;
        const latLng = { lat: location.lat(), lng: location.lng() };
        
        setMarkerPosition(latLng);
        setSearchQuery(result.description);
        setSearchResults([]);

        // Pan map to location
        if (mapRef.current) {
          mapRef.current.flyTo([latLng.lat, latLng.lng], 17);
        }
      }
    });
  };

  // Get current GPS location
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('La geolocalización no está soportada en este dispositivo');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const latLng = { lat: latitude, lng: longitude };
        
        setMarkerPosition(latLng);
        setError('');

        if (mapRef.current) {
          mapRef.current.flyTo([latitude, longitude], 17);
        }
      },
      (error) => {
        console.error('Error getting location:', error);
        setError('No se pudo obtener tu ubicación actual');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  // Handle map click
  const handleMapClick = (latlng) => {
    setMarkerPosition({ lat: latlng.lat, lng: latlng.lng });
    setError('');
  };

  // Save location to backend
  const handleSaveLocation = async () => {
    if (!markerPosition) {
      setError('Por favor selecciona una ubicación en el mapa');
      return;
    }

    setShowConfirmModal(false);
    setIsSaving(true);
    setError('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/driver_final_location`, {
        addresses: [{
          latitude: markerPosition.lat,
          longitude: markerPosition.lng,
        }],
        driver_id: userId,
      });

      // Success - go back
      setActiveComponent("OptionsDriver");
    } catch (err) {
      console.error('Error saving location:', err);
      setError('Error al guardar la ubicación. Por favor intenta de nuevo.');
      setIsSaving(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Fixed Header */}
      <header className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm z-[1000]">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setActiveComponent('OptionsDriver')}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              disabled={isSaving}
            >
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-extrabold text-brand-primary font-montserrat">
                Ubicación del Conductor
              </h1>
              <p className="text-xs text-slate-600 font-montserrat">
                Selecciona tu ubicación actual
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Search Bar */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 p-3 shadow-sm z-[999]">
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Buscar dirección..."
              className="w-full pl-10 pr-4 py-3 border-2 border-slate-300 rounded-lg focus:border-brand-primary focus:outline-none font-montserrat text-sm"
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-primary animate-spin" />
            )}
          </div>

          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
              {searchResults.map((result) => (
                <button
                  key={result.place_id}
                  onClick={() => handleResultClick(result)}
                  className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-brand-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700 font-montserrat">
                      {result.description}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Map Container */}
      <div className="flex-1 relative">
        <MapContainer
          center={defaultCenter}
          zoom={13}
          className="h-full w-full"
          ref={mapRef}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <MapClickHandler onLocationSelect={handleMapClick} />
          
          {markerPosition && (
            <Marker
              position={[markerPosition.lat, markerPosition.lng]}
              icon={customMarkerIcon}
            />
          )}
        </MapContainer>

        {/* GPS Button */}
        <button
          onClick={handleGetCurrentLocation}
          className="absolute bottom-32 right-4 p-3 bg-white rounded-full shadow-lg hover:bg-slate-50 transition-colors z-[400] border-2 border-brand-primary"
          aria-label="Obtener ubicación actual"
        >
          <Navigation className="w-6 h-6 text-brand-primary" />
        </button>

        {/* Info Badge */}
        {markerPosition && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white px-4 py-2 rounded-full shadow-lg z-[400] border border-slate-200">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-xs font-medium text-slate-700 font-montserrat">
                Ubicación seleccionada
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Action Panel */}
      <div className="flex-shrink-0 bg-white border-t border-slate-200 shadow-lg p-4">
        {error && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 font-montserrat">{error}</p>
          </div>
        )}

        {markerPosition && (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800 font-montserrat">
              <strong>Coordenadas:</strong> {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
            </p>
          </div>
        )}

        <button
          onClick={() => setShowConfirmModal(true)}
          disabled={!markerPosition || isSaving}
          className="w-full py-3 bg-brand-primary text-white rounded-lg font-bold font-montserrat hover:bg-brand-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Guardando...</span>
            </>
          ) : (
            <>
              <MapPin className="w-5 h-5" />
              <span>Guardar Ubicación</span>
            </>
          )}
        </button>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 font-montserrat mb-2">
                Confirmar Ubicación
              </h3>
              <p className="text-sm text-gray-600 font-montserrat">
                ¿Está seguro de que esta es su ubicación actual correcta?
              </p>
              {markerPosition && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-700 font-montserrat">
                    <strong>Lat:</strong> {markerPosition.lat.toFixed(6)}<br />
                    <strong>Lng:</strong> {markerPosition.lng.toFixed(6)}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-2.5 bg-gray-200 text-gray-700 rounded-lg font-medium font-montserrat hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveLocation}
                className="flex-1 py-2.5 bg-brand-primary text-white rounded-lg font-bold font-montserrat hover:bg-brand-primary/90 transition-colors"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MapSelectionDriver;